/**
 * confidence - the model's certainty, said in words.
 *
 * A finding carries `confidence_score`, and the canvas used to print it as a
 * percentage. "87%" invites arithmetic that the number cannot support: it is
 * not a probability anybody calibrated, and two findings at 84% and 87% are
 * not meaningfully different. A BAND is the honest reading of it - high,
 * medium, low - and it is the only thing a reviewer acts on.
 *
 * Risk stays numeric. Risk is scored on a scale the playbook defines and the
 * difference between 6 and 8 is real; confidence is not that kind of number.
 *
 * DEFENSIVE NORMALISATION. The API sends 0–1. Some payloads have historically
 * sent 0–100, and a 0.87 read as "87 out of 1" would band every finding high,
 * so anything above 1 is treated as a percentage. Nothing is invented for a
 * MISSING score: null bands to null, which the canvas renders as "—". Banding
 * an absent score as Low would be a claim about the finding.
 */

export type ConfidenceBand = 'high' | 'medium' | 'low';

/** High ≥ 0.80 · Medium 0.50–0.79 · Low < 0.50. */
export const CONFIDENCE_THRESHOLDS = { high: 0.8, medium: 0.5 } as const;

/**
 * The band for a raw score, or null when there is nothing to band.
 *
 * Accepts the unnormalised shapes the field arrives in - undefined, null, a
 * numeric string, a 0–100 percentage - because this is the ONE place that
 * decides what a confidence score means.
 */
export const confidenceBand = (
  score: number | string | null | undefined,
): ConfidenceBand | null => {
  if (score === null || score === undefined || score === '') return null;

  const raw = typeof score === 'number' ? score : Number(score);
  if (!Number.isFinite(raw)) return null;

  // A score above 1 can only be a percentage; below 0 is meaningless.
  const value = raw > 1 ? raw / 100 : raw;
  if (value < 0) return null;

  if (value >= CONFIDENCE_THRESHOLDS.high) return 'high';
  if (value >= CONFIDENCE_THRESHOLDS.medium) return 'medium';
  return 'low';
};

/**
 * The colour of each band.
 *
 * Low is MUTED, not red: a low-confidence finding is not a dangerous one, it
 * is one the model is unsure about. Danger is severity's colour and lending
 * it to confidence would have every uncertain finding read as critical.
 */
export const CONFIDENCE_TONE: Record<ConfidenceBand, string> = {
  high: 'text-success-600 dark:text-success-400',
  medium: 'text-warning-600 dark:text-warning-400',
  low: 'text-fg-muted',
};

/**
 * The English label for a band - the i18n KEY, translated at the call site.
 *
 * Kept here so "High" cannot be spelled three ways across the finding header,
 * the findings list and the in-document card.
 */
export const CONFIDENCE_LABEL: Record<ConfidenceBand, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/** What a missing score renders as. Never a band. */
export const CONFIDENCE_EMPTY = '—';
